import { omit } from 'remeda';
import z from 'zod';

import { ParagraphCodec, PlateParagraph, UnityParagraph } from './Paragraph';
import {
    CURRENT_VERSION,
    ElementId,
    emptyChildren,
    EmptyChildren,
    UnityElementId,
    Version,
} from '../commons';
import { ELEMENT_TYPES, MIME_TYPES } from '../constants';

export const UnityTiktok = z.object({
    type: z.literal(ELEMENT_TYPES.TIKTOK),
    mimetype: z.literal(MIME_TYPES.TIKTOK),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    originalSrc: z.string().nullish(),
    src: z.string(),
    contentAttributes: z.object({
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullish(),
        credit: z.string().nullish(),
    }),
});
type UnityTiktok = z.infer<typeof UnityTiktok>;

export const PlateTiktok = z.object({
    type: z.literal(ELEMENT_TYPES.TIKTOK),
    id: ElementId,
    data: z.object({
        mimetype: z.literal(MIME_TYPES.TIKTOK),
        templateElement: z.boolean().optional(),
        originalSrc: z.string().nullish(),
        src: z.string(),
        embed: z.object({
            caption: z.array(PlateParagraph),
            credit: z.string(),
        }),
    }),
    children: EmptyChildren,
});
export type TiktokElement = z.infer<typeof PlateTiktok>;

export const TiktokCodec = z.codec(UnityTiktok, PlateTiktok, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            mimetype: value.mimetype,
            originalSrc: value.originalSrc,
            src: value.src,
            embed: {
                caption: value.contentAttributes.caption
                    ? value.contentAttributes.caption.richTextFormat.map((paragraph) =>
                          ParagraphCodec.decode(paragraph),
                      )
                    : [
                          {
                              type: ELEMENT_TYPES.PARAGRAPH,
                              id: crypto.randomUUID(),
                              children: emptyChildren,
                          },
                      ],
                credit: value.contentAttributes.credit ?? '',
            },
            ...(value.templateElement && { templateElement: value.templateElement }),
        },
        children: emptyChildren,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        ...omit(value.data, ['embed']),
        contentAttributes: {
            caption: {
                richTextFormat: value.data.embed.caption.map((paragraph) =>
                    ParagraphCodec.encode(paragraph),
                ),
            },
            credit: value.data.embed.credit,
        },
    }),
});
